import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Loader2, TrendingUp, Sparkles, ArrowRight, Lock, Shield, ShieldCheck,
  FileBarChart2, Clock, Database,
} from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { ensurePixelReady, trackEvent as trackMetaEvent } from "../lib/metaPixel";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";

/**
 * V229 — Competitor Financial Benchmark landing (/benchmark)
 *
 * Entire funnel bets on ONE insight Zenith validated with their FB ads:
 * SK CEOs will pay for competitor financial data they can see is sitting
 * in a public register but can't be bothered to look up themselves.
 *
 * Pricing: €14 early-bird (first 100 reports) → €27 regular.
 * Delivery: PDF within 24h to buyer's email.
 * Data: ORSF.sk + registeruz.sk (both free public SK APIs, CC-BY 4.0 / CC0).
 */

export default function BenchmarkLandingPage() {
  const [clientDomain, setClientDomain] = useState("");
  const [competitor1, setCompetitor1] = useState("");
  const [competitor2, setCompetitor2] = useState("");
  const [competitor3, setCompetitor3] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState({
    regular_price_eur: 27,
    early_bird_price_eur: 14,
    delivery_hours: 24,
  });

  useEffect(() => {
    trackEvent("view_benchmark_offer", {
      value: 14, currency: "EUR", item_id: "benchmark_report",
      source: "benchmark_14_landing",
    });
    ensurePixelReady().then((ok) => {
      if (ok) {
        trackMetaEvent("ViewContent", {
          value: 14, currency: "EUR",
          content_ids: ["benchmark_report"],
          content_type: "product",
          content_name: "Competitor Financial Benchmark",
        });
      }
    });
    axios.get(`${API}/benchmark/pricing`).then((r) => {
      if (r.data) setPricing((p) => ({ ...p, ...r.data }));
    }).catch(() => {});
  }, []);

  const canSubmit =
    clientDomain.trim().length > 3 &&
    competitor1.trim().length > 3 &&
    email.trim().includes("@");

  const handleCheckout = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    trackEvent("start_checkout", {
      value: 14, currency: "EUR", item_id: "benchmark_report",
      source: "benchmark_14_landing",
    });
    trackMetaEvent("InitiateCheckout", {
      value: 14, currency: "EUR",
      content_ids: ["benchmark_report"],
      content_type: "product",
      email: email.trim(),
    }).catch(() => {});

    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "benchmark_report",
        origin_url: window.location.origin,
        success_url: "/benchmark/success",
        cancel_url: "/benchmark",
        email: email.trim(),
        language: "sk",
        metadata: {
          source: "benchmark_14_landing",
          client_domain: clientDomain.trim(),
          competitor_1: competitor1.trim(),
          competitor_2: competitor2.trim(),
          competitor_3: competitor3.trim(),
        },
      });
      // Pre-register the request so backend can process after Stripe webhook
      await axios.post(`${API}/benchmark/request`, {
        client_domain: clientDomain.trim(),
        competitor_1: competitor1.trim(),
        competitor_2: competitor2.trim() || null,
        competitor_3: competitor3.trim() || null,
        email: email.trim(),
        language: "sk",
      }).catch(() => {});
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      console.error("checkout failed", e);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden" data-testid="benchmark-landing">
      <header className="px-6 py-6 max-w-5xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 group" data-testid="benchmark-home-link">
          <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
        </a>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Shield className="w-3 h-3" />
          GDPR · 256-bit SSL
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-8 pb-16 max-w-3xl mx-auto" data-testid="benchmark-hero">
        <span className="inline-block px-3 py-1 bg-[#FFE600]/10 text-[#FFE600] text-xs font-bold tracking-widest uppercase mb-6 font-['JetBrains_Mono']">
          COMPETITOR FINANCIAL BENCHMARK · €{pricing.early_bird_price_eur}
        </span>

        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tight leading-[0.95] mb-6"
          style={{ textWrap: "balance" }}
          data-testid="benchmark-h1"
        >
          Koľko zarobil váš <span className="text-[#a855f7]">konkurent</span>{" "}
          minulý rok?
        </h1>

        <p className="text-xl text-gray-300 mb-8 max-w-xl" data-testid="benchmark-subhead">
          Dostanete PDF s <strong className="text-white">obratom, čistou maržou, čistým ziskom a trendom</strong> 3 slovenských konkurentov. Dáta zo
          Slovenského obchodného registra. Priamo do emailu do 24 hodín.
        </p>

        {/* Form */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 font-['JetBrains_Mono']">
            Vaša doména
          </label>
          <input
            type="text"
            value={clientDomain}
            onChange={(e) => setClientDomain(e.target.value)}
            placeholder="firma.sk"
            data-testid="benchmark-client-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 mb-4 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
          />
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 font-['JetBrains_Mono']">
            Konkurenti (až 3 — stačí 1)
          </label>
          {[
            [competitor1, setCompetitor1, "konkurent1.sk", "comp-1"],
            [competitor2, setCompetitor2, "konkurent2.sk (voliteľné)", "comp-2"],
            [competitor3, setCompetitor3, "konkurent3.sk (voliteľné)", "comp-3"],
          ].map(([val, setter, placeholder, tid]) => (
            <input
              key={tid}
              type="text"
              value={val}
              onChange={(e) => setter(e.target.value)}
              placeholder={placeholder}
              data-testid={`benchmark-${tid}-input`}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 mb-2 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
            />
          ))}
          <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2 mt-4 font-['JetBrains_Mono']">
            Kam doručiť PDF
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="vy@firma.sk"
            data-testid="benchmark-email-input"
            className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 mb-5 text-white placeholder-gray-600 focus:border-[#a855f7] focus:outline-none"
          />

          <div
            data-testid="benchmark-guarantee-badge"
            className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-3 mb-5"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-emerald-300">
                14-dní garancia vrátenia peňazí
              </p>
              <p className="text-[11px] text-emerald-200/70 leading-snug mt-0.5">
                Ak report neprináša insight, vrátime €{pricing.early_bird_price_eur}. Bez otázok.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCheckout}
            disabled={!canSubmit || submitting}
            data-testid="benchmark-checkout-btn"
            className="w-full bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-50 disabled:cursor-not-allowed text-white font-black py-4 rounded-lg flex items-center justify-center gap-2 text-lg transition-all"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Získať benchmark za €{pricing.early_bird_price_eur} <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <div
            data-testid="benchmark-trust-strip"
            className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5"
          >
            <TrustItem icon={Lock} label="Stripe secure" />
            <TrustItem icon={Clock} label="PDF do 24h" />
            <TrustItem icon={ShieldCheck} label="14-dní garancia" />
          </div>

          <div
            data-testid="benchmark-sk-trust-strip"
            className="flex items-center justify-center gap-2 flex-wrap mt-3 pt-3 border-t border-white/5 text-[11px] text-gray-400"
          >
            <span className="inline-flex items-center gap-1">
              <span className="text-base leading-none">🇸🇰</span>
              <span>sídlo RESTOB s.r.o., Bratislava</span>
            </span>
            <span className="opacity-30">·</span>
            <span>Dáta: ORSF.sk + RÚZ (CC-BY 4.0)</span>
            <span className="opacity-30">·</span>
            <span>Early-bird prvých 100 reportov</span>
          </div>
        </div>

        {/* Price anchor */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FFE600]" />
            <span>
              Normal €{pricing.regular_price_eur} ·{" "}
              <span className="text-[#a855f7] font-bold">Dnes €{pricing.early_bird_price_eur}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span>50 % early-bird zľava</span>
          </div>
          <a href="/benchmark/sample" className="text-xs underline hover:text-white"
             data-testid="benchmark-sample-link">
            → ukážka reportu
          </a>
        </div>
      </section>

      {/* Deliverables */}
      <section className="px-6 pb-20 max-w-3xl mx-auto" data-testid="benchmark-deliverables">
        <h2 className="text-2xl font-black mb-8 font-['Cabinet_Grotesk']">Čo získate v PDF reporte</h2>
        <ol className="space-y-4">
          {[
            [FileBarChart2, "Finančný prehľad", "Obrat, čistá marža, čistý zisk a hospodársky výsledok pre vás a 3 konkurentov za posledné 3 účtovné obdobia."],
            [TrendingUp, "Trend tabuľka", "Vizualizácia rast / pokles obratu a zisku medzi rokmi — vidíte kto raste a kto klesá."],
            [Database, "Verejné dáta, transparentné zdroje", "Všetky čísla pochádzajú z ORSF.sk + registeruz.sk (SK štátny register). Zdroje citované v reporte."],
            [Sparkles, "AI Visibility bonus", "Zdarma pridávame AI Visibility skóre každej firmy (ChatGPT, Perplexity, Gemini citácie) — exkluzívne naše dáta, nikde inde nedostanete."],
          ].map(([Icon, title, desc], i) => (
            <li
              key={title}
              data-testid={`benchmark-deliverable-${i}`}
              className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl p-5"
            >
              <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#a855f7]/15 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#a855f7]" />
              </span>
              <div>
                <h3 className="font-bold mb-1">{title}</h3>
                <p className="text-sm text-gray-400">{desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Agency vs us */}
      <section className="px-6 pb-20 max-w-3xl mx-auto" data-testid="benchmark-vs-agency">
        <div className="bg-gradient-to-br from-[#a855f7]/15 via-[#a855f7]/5 to-transparent border border-[#a855f7]/30 rounded-2xl p-6 sm:p-8">
          <p className="text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            VS. KLASICKÁ AGENTÚRA
          </p>
          <h2 className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5">
            SK agentúra: <span className="text-[#a855f7]">€1 500 /mes</span>
            <br />
            TopRankPulse: <span className="text-emerald-400">€{pricing.early_bird_price_eur} raz</span>
          </h2>
          <p className="text-sm text-gray-300 mb-4 max-w-xl">
            Zenith Slovakia a podobné agentúry si manuálne zostavujú rovnaký benchmark report v Google
            Slides a predávajú ho ako lead magnet pre svoje €1 500+/mes agentúrne balíčky. My to robíme{" "}
            <strong>automaticky z verejných dát</strong> a predávame samotný report
            za 100-násobne nižšiu cenu.
          </p>
          <p className="text-[11px] text-gray-500">
            Priemerná cena full-service SK digitálnej agentúry: €1 500 – €5 000/mes
            (zdroj: verejné cenníky Viziora, Zenith, Visibility, Visibility&Boost).
          </p>
        </div>
      </section>

      <section className="px-6 pb-20 max-w-5xl mx-auto">
        <OrderNowBottomCTA
          packageId="benchmark_report"
          priceEur={14}
          interval="jednorazový"
          serviceName="Benchmark Report"
          headline="Pre-order Benchmark Report"
          subcopy="Porovnanie voči 3 konkurentom. 48h delivery. Jednorazová platba €14 — bez auto-renewal."
          testid="benchmark-final-cta"
        />
      </section>
    </div>
  );
}

function TrustItem({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 text-center">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}
